import { AppDispatchNext } from 'actions/actions.typesNext'
import { goBackAction, pushModalAction } from 'actions/route/routeAction'
import { FetchInitialPaymentDataAction } from 'actions/storefront/fetchInitialPaymentDataAction'
import { FetchPaymentFormAction } from 'actions/storefront/fetchPaymentFormAction'
import { fetchPaymentTypeListAction } from 'actions/storefront/fetchPaymentTypeListAction'
import { StopChatInStorefrontAction } from 'actions/storefront/fetchStopChatInStorefrontAction'
import { performResultAction } from 'actions/storefront/performResultAction'
import { setPaymentReceiptAction } from 'actions/storefront/setPaymentReceiptAction'
import { storefrontGenericErrorAction } from 'actions/storefront/storefrontGenericErrorAction'
import { AsyncThunkAction } from 'actions/types'
import {
  ResponseParametersGet,
  ResponseParametersPost,
  requestPaymentApi,
} from 'api/billing/billingApi'
import { Tariff } from 'api/billing/fetchTariffsApi'
import { ApiResult } from 'api/fetchApi'
import { hasReceipt } from 'api/storefront/function/hasReceipt'
import { Product } from 'api/storefront/storefront.types'
import { DeprecatedApi6Error } from 'api/types'
import { storeFrontPath } from 'components/paths'
import {
  PaymentType,
  StorefrontService,
} from 'components/storefront/StoreFront.constants'
import { findTariff } from 'components/storefront/StoreFront.functions'
import {
  orderPath,
  paymentFormPath,
  storeFrontTopUpPath,
} from 'components/storefront/StoreFront.paths'
import { hasClientOrServerError } from 'functions/api/hasClientOrServerError'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { replace } from 'functions/router'

export const TOGGLE_PAYMENT_LOADING = 'TOGGLE_PAYMENT_LOADING' as const

export const togglePaymentLoadingAction = (loading: boolean) => ({
  type: TOGGLE_PAYMENT_LOADING,
  loading,
})

export const TOGGLE_EXPAND_IFRAME_ACTION =
  'TOGGLE_EXPAND_IFRAME_ACTION' as const

export const toggleExpandIframeAction = (expand: boolean) => ({
  type: TOGGLE_EXPAND_IFRAME_ACTION,
  expand,
})

export const REQUEST_PAYMENT = 'REQUEST_PAYMENT' as const

export const requestPaymentOrderIdPlainAction = (
  paymentType: string,
  orderId: string
) => ({
  type: REQUEST_PAYMENT,
  promise: () => requestPaymentApi(paymentType, orderId),
})

export const commonPaymentAction =
  (
    storeFrontType: StorefrontService,
    product: Product,
    result: { ok: boolean } & DeprecatedApi6Error
  ): AsyncThunkAction =>
  (dispatch, getState) => {
    /**
     * Если прилетел нотайс, значит не нужно показывать шорткат.
     * https://youtrack.mamba.ru/issue/M-8471
     */
    if (result.internalError?.notice) {
      return
    }

    const {
      paymentStorefrontReducer: { tariffs, paymentOrderId },
    } = getState()

    if (result.ok) {
      const tariff = findTariff(product, tariffs)
      if (tariff) {
        return dispatch(commonPaymentOrderWithResult(storeFrontType, tariff))
      } else {
        console.error(`"${storeFrontType}" tariff was not found.`)
      }
    }
    return dispatch(
      performResultAction(storeFrontType, paymentOrderId as string, false)
    )
  }

const commonPaymentOrderWithResult =
  (storefrontType: StorefrontService, tariff: Tariff): AsyncThunkAction =>
  (dispatch, getState) => {
    const {
      paymentStorefrontReducer: { paymentOrderId, instantPaymentEnabled },
    } = getState()
    if (tariff.instantPayment && instantPaymentEnabled) {
      const { paymentType } = tariff.instantPayment
      return dispatch(
        requestPaymentOrderIdWithResultAction(
          storefrontType,
          paymentType,
          paymentOrderId as string
        )
      )
    } else {
      dispatch(moveOrderPaymentAction(storefrontType))
    }
    return Promise.resolve()
  }

const moveOrderPaymentAction =
  (storefrontType: string): AsyncThunkAction =>
  async (dispatch, getState) => {
    const {
      systemReducer: { baseUrl },
      paymentStorefrontReducer: { paymentOrderId },
    } = getState()

    const response = (await dispatch(
      fetchPaymentTypeListAction(paymentOrderId as string)
    )) as unknown as ApiResult<ResponseParametersGet>

    /**
     * В данном случае мы просто код ответа, и если там ошибка
     * то генерим ошибку.
     */
    if (
      hasClientOrServerError(response) ||
      !getState().paymentStorefrontReducer.paymentTypes
    ) {
      dispatch(storefrontGenericErrorAction())
      return
    }

    if (
      getState().paymentStorefrontReducer.paymentTypes[0].type ===
      PaymentType.Coins
    ) {
      return dispatch(pushModalAction(storeFrontTopUpPath))
    }
    dispatch(
      // https://redmine.mamba.ru/issues/104994
      // push(
      replace(
        mergeAllUrls(
          baseUrl,
          storeFrontPath,
          storefrontType,
          paymentFormPath,
          orderPath,
          paymentOrderId!
        )
      )
    )

    dispatch(togglePaymentLoadingAction(false))
  }

export const requestPaymentOrderIdWithResultAction =
  (
    storeFrontType: StorefrontService,
    paymentType: PaymentType,
    orderId: string
  ) =>
  async (dispatch: AppDispatchNext) => {
    const response = (await dispatch(
      requestPaymentOrderIdPlainAction(paymentType, orderId)
    )) as unknown as ApiResult<ResponseParametersPost>
    const { result, ok, error } = response
    /**
     * В данном случае мы просто код ответа, и если там ошибка
     * то генерим ошибку.
     */
    if (hasClientOrServerError(response)) {
      dispatch(storefrontGenericErrorAction())
      dispatch(togglePaymentLoadingAction(false))
      return
    }

    dispatch(setPaymentReceiptAction(result?.receipt!))

    /**
     * Когда есть receipt и это карты или когда это другой вид оплаты, то будет стандартно
     * Если нет receipt, а это карты, то просто закрываем, и ждем из кометы сообщение
     * https://redmine.mamba.ru/issues/110111
     */
    const hasNoticeToast =
      (result?.receipt && paymentType === PaymentType.BankCards) ||
      paymentType !== PaymentType.BankCards

    if (hasNoticeToast) {
      dispatch(
        performResultAction(
          storeFrontType,
          orderId,
          ok || hasReceipt(result?.receipt!),
          paymentType
        )
      )
    } else {
      if (!error) {
        dispatch(goBackAction())
      }
    }

    dispatch(togglePaymentLoadingAction(false))
  }

export const SELECT_OPERATOR = 'SELECT_OPERATOR' as const

export const selectSmsOperatorAction = (index: number) => ({
  type: SELECT_OPERATOR,
  index,
})

export const RESET_PAYMENT_SUCCESSFUL = 'RESET_PAYMENT_SUCCESSFUL' as const

export const resetPaymentSuccessfulAction = () => ({
  type: RESET_PAYMENT_SUCCESSFUL,
})

export const TOGGLE_INSTANT_PAYMENT = 'TOGGLE_INSTANT_PAYMENT' as const

export const toggleInstantPaymentAction = (enabled: boolean) => ({
  type: TOGGLE_INSTANT_PAYMENT,
  enabled,
})

export type PaymentStorefrontActionTypes =
  | FetchInitialPaymentDataAction
  | StopChatInStorefrontAction
  | FetchPaymentFormAction
  | ReturnType<typeof togglePaymentLoadingAction>
  | ReturnType<typeof toggleExpandIframeAction>
  | ReturnType<typeof requestPaymentOrderIdPlainAction>
  | ReturnType<typeof selectSmsOperatorAction>
  | ReturnType<typeof toggleInstantPaymentAction>
